function readFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readArray(value) {
  return Array.isArray(value) ? value : [];
}

function readObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : null;
}

function toFiniteNumberArray(values) {
  return readArray(values).filter(
    (value) => typeof value === "number" && Number.isFinite(value),
  );
}

function pickRespiratoryPerBreathEntries(docData) {
  const respiratoryRate = docData?.results?.mpm?.respiratory_rate ?? null;

  return readArray(
    respiratoryRate?.perBreath ??
      respiratoryRate?.per_breath ??
      respiratoryRate?.breaths ??
      respiratoryRate?.breaths_per_minute_series,
  )
    .map((entry) => readObject(entry))
    .filter(Boolean)
    .filter((entry) => entry.excluded !== true);
}

function buildIntervalsFromTimes(times) {
  const finiteTimes = toFiniteNumberArray(times);
  if (finiteTimes.length < 2) return [];

  const intervals = [];

  for (let index = 0; index < finiteTimes.length - 1; index += 1) {
    const current = finiteTimes[index];
    const next = finiteTimes[index + 1];
    const interval = next - current;

    if (interval > 0 && Number.isFinite(interval)) {
      intervals.push(interval);
    }
  }

  return intervals;
}

function buildObservedSeriesFromTimes(times) {
  const finiteTimes = toFiniteNumberArray(times);
  if (finiteTimes.length < 2) return [];

  const firstTime = finiteTimes[0];
  const points = [];

  for (let index = 0; index < finiteTimes.length - 1; index += 1) {
    const current = finiteTimes[index];
    const next = finiteTimes[index + 1];
    const durationSec = next - current;

    if (!(durationSec > 0) || !Number.isFinite(durationSec)) continue;

    points.push({
      t: current - firstTime,
      observed: 60 / durationSec,
    });
  }

  return points;
}

function buildObservedSeriesFromIntervals(intervals) {
  const finiteIntervals = toFiniteNumberArray(intervals).filter(
    (value) => value > 0,
  );

  if (!finiteIntervals.length) return [];

  let elapsedSeconds = 0;

  return finiteIntervals.map((durationSec) => {
    const point = {
      t: elapsedSeconds,
      observed: 60 / durationSec,
    };

    elapsedSeconds += durationSec;
    return point;
  });
}

function buildObservedSeriesFromRelativeTimesAndRr(times, rrValues) {
  const finiteTimes = toFiniteNumberArray(times);
  const finiteRrValues = readArray(rrValues);
  const pointCount = Math.min(finiteTimes.length, finiteRrValues.length);

  if (!pointCount) return [];

  const points = [];

  for (let index = 0; index < pointCount; index += 1) {
    const t = readFiniteNumber(finiteTimes[index]);
    const observed = readFiniteNumber(finiteRrValues[index]);

    if (t == null || observed == null) continue;

    points.push({ t, observed });
  }

  return points;
}

function pickProcessedSignalTimes(docData) {
  const processed =
    docData?.["mpm-signal-processed"] ||
    docData?.mpmSignalProcessed ||
    docData?.mpm_signal_processed ||
    null;

  if (!processed) return [];

  return toFiniteNumberArray(
    processed.resampledTimesSeconds ??
      processed.timesSeconds ??
      processed.timeSeconds ??
      processed.time_seconds,
  );
}

function pickPeakTimesFromRespiratoryRate(docData) {
  const respiratoryRate = docData?.results?.mpm?.respiratory_rate ?? null;

  return toFiniteNumberArray(
    respiratoryRate?.peakTimesSeconds ??
      respiratoryRate?.peak_times_seconds ??
      respiratoryRate?.peakTimes ??
      respiratoryRate?.peak_times,
  );
}

function pickPeakTimesFromProcessedSignal(docData) {
  const respiratoryRate = docData?.results?.mpm?.respiratory_rate ?? null;
  const peakIndices = toFiniteNumberArray(
    respiratoryRate?.peakIndices ?? respiratoryRate?.peak_indices,
  );
  const signalTimes = pickProcessedSignalTimes(docData);

  if (!peakIndices.length || !signalTimes.length) return [];

  return peakIndices
    .map((peakIndex) => {
      const roundedIndex = Math.round(peakIndex);
      return readFiniteNumber(signalTimes[roundedIndex]);
    })
    .filter((value) => value != null);
}

export function buildPoincareFromIntervals(intervals) {
  if (!Array.isArray(intervals) || intervals.length < 2) {
    return { x: [], y: [], maxInterval: null };
  }

  const x = [];
  const y = [];
  let maxInterval = null;

  for (let index = 0; index < intervals.length - 1; index += 1) {
    const current = intervals[index];
    const next = intervals[index + 1];

    if (
      typeof current === "number" &&
      Number.isFinite(current) &&
      typeof next === "number" &&
      Number.isFinite(next)
    ) {
      x.push(current);
      y.push(next);
      maxInterval = Math.max(maxInterval ?? 0, current, next);
    }
  }

  return { x, y, maxInterval };
}

export function pickRespiratoryIntervalsSeconds(docData) {
  const respiratoryRate = docData?.results?.mpm?.respiratory_rate ?? null;
  const breathingRecovery = docData?.results?.mpm?.breathing_recovery ?? null;
  const perBreathEntries = pickRespiratoryPerBreathEntries(docData);

  if (perBreathEntries.length >= 2) {
    const intervalsFromPerBreath = perBreathEntries
      .map((entry) => readFiniteNumber(entry.intervalSeconds))
      .filter((value) => value != null && value > 0);

    if (intervalsFromPerBreath.length >= 2) {
      return intervalsFromPerBreath;
    }
  }

  const directSecondsCandidates = [
    respiratoryRate?.peakIntervalsSeconds,
    respiratoryRate?.peak_intervals_seconds,
    respiratoryRate?.breathIntervalsSeconds,
    respiratoryRate?.breath_intervals_seconds,
    respiratoryRate?.interBreathIntervalsSeconds,
    respiratoryRate?.inter_breath_intervals_seconds,
    respiratoryRate?.intervalsSeconds,
    respiratoryRate?.intervals_seconds,
  ];

  for (const candidate of directSecondsCandidates) {
    const values = toFiniteNumberArray(candidate).filter((value) => value > 0);
    if (values.length >= 2) return values;
  }

  const directMillisecondsCandidates = [
    respiratoryRate?.peakIntervalsMs,
    respiratoryRate?.peak_intervals_ms,
    respiratoryRate?.breathIntervalsMs,
    respiratoryRate?.breath_intervals_ms,
    respiratoryRate?.interBreathIntervalsMs,
    respiratoryRate?.inter_breath_intervals_ms,
    respiratoryRate?.intervalsMs,
    respiratoryRate?.intervals_ms,
  ];

  for (const candidate of directMillisecondsCandidates) {
    const values = toFiniteNumberArray(candidate)
      .map((value) => value / 1000)
      .filter((value) => value > 0);

    if (values.length >= 2) return values;
  }

  const observedBreathsPerMinute = toFiniteNumberArray(
    breathingRecovery?.observedBreathsPerMinute ??
      breathingRecovery?.observed_breaths_per_minute,
  );

  if (observedBreathsPerMinute.length >= 2) {
    const intervalsFromObservedRr = observedBreathsPerMinute
      .map((breathsPerMinute) => 60 / breathsPerMinute)
      .filter((value) => Number.isFinite(value) && value > 0);

    if (intervalsFromObservedRr.length >= 2) {
      return intervalsFromObservedRr;
    }
  }

  const recoveryTimesCandidates = [
    breathingRecovery?.observedRelativeTimesSeconds,
    breathingRecovery?.observed_relative_times_seconds,
    breathingRecovery?.observedTimesSeconds,
    breathingRecovery?.observed_times_seconds,
  ];

  for (const candidate of recoveryTimesCandidates) {
    const intervals = buildIntervalsFromTimes(candidate);
    if (intervals.length >= 2) return intervals;
  }

  const peakTimesFromRespiratoryRate = pickPeakTimesFromRespiratoryRate(docData);
  const peakIntervalsFromRespiratoryRate = buildIntervalsFromTimes(
    peakTimesFromRespiratoryRate,
  );
  if (peakIntervalsFromRespiratoryRate.length >= 2) {
    return peakIntervalsFromRespiratoryRate;
  }

  const peakTimes = pickPeakTimesFromProcessedSignal(docData);
  const peakIntervals = buildIntervalsFromTimes(peakTimes);
  if (peakIntervals.length >= 2) return peakIntervals;

  return [];
}

export function pickRespiratoryPoincare(docData) {
  return buildPoincareFromIntervals(pickRespiratoryIntervalsSeconds(docData));
}

export function pickRespiratoryObservedRrSeries(docData) {
  const respiratoryRate = docData?.results?.mpm?.respiratory_rate ?? null;
  const breathingRecovery = docData?.results?.mpm?.breathing_recovery ?? null;
  const perBreathEntries = pickRespiratoryPerBreathEntries(docData);

  if (perBreathEntries.length) {
    const firstTime =
      readFiniteNumber(perBreathEntries[0]?.startTimeSeconds) ??
      readFiniteNumber(perBreathEntries[0]?.endTimeSeconds) ??
      0;

    const pointsFromPerBreath = perBreathEntries
      .map((entry, index) => {
        const observed = readFiniteNumber(entry.breathsPerMinute);
        const absoluteTime =
          readFiniteNumber(entry.startTimeSeconds) ??
          readFiniteNumber(entry.endTimeSeconds) ??
          index;

        if (observed == null || absoluteTime == null) return null;

        return {
          t: absoluteTime - firstTime,
          observed,
        };
      })
      .filter(Boolean);

    if (pointsFromPerBreath.length) return pointsFromPerBreath;
  }

  const timeCandidates = [
    breathingRecovery?.observedRelativeTimesSeconds,
    breathingRecovery?.observed_relative_times_seconds,
    respiratoryRate?.observedRelativeTimesSeconds,
    respiratoryRate?.observed_relative_times_seconds,
    respiratoryRate?.timesSeconds,
    respiratoryRate?.times_seconds,
  ];

  const rrCandidates = [
    breathingRecovery?.observedBreathsPerMinute,
    breathingRecovery?.observed_breaths_per_minute,
    respiratoryRate?.observedBreathsPerMinute,
    respiratoryRate?.observed_breaths_per_minute,
    respiratoryRate?.breathsPerMinute,
    respiratoryRate?.breaths_per_minute,
    respiratoryRate?.rrPerBreath,
    respiratoryRate?.rr_per_breath,
  ];

  for (const times of timeCandidates) {
    for (const rrValues of rrCandidates) {
      const points = buildObservedSeriesFromRelativeTimesAndRr(times, rrValues);
      if (points.length) return points;
    }
  }

  const peakTimesFromRespiratoryRate = pickPeakTimesFromRespiratoryRate(docData);
  const pointsFromRespiratoryRatePeakTimes = buildObservedSeriesFromTimes(
    peakTimesFromRespiratoryRate,
  );
  if (pointsFromRespiratoryRatePeakTimes.length) {
    return pointsFromRespiratoryRatePeakTimes;
  }

  const peakTimes = pickPeakTimesFromProcessedSignal(docData);
  const pointsFromPeakTimes = buildObservedSeriesFromTimes(peakTimes);
  if (pointsFromPeakTimes.length) return pointsFromPeakTimes;

  return buildObservedSeriesFromIntervals(pickRespiratoryIntervalsSeconds(docData));
}
